import { BookOpen, CalendarRange, FileText, FolderOpen, Globe, HardDrive, type LucideIcon } from "lucide-react";
import { type ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { formatList } from "@/i18n/format";
import type { InternalSource, ResearchProposal } from "./researchModel";

const INTERNAL_ICONS: Record<InternalSource, LucideIcon> = {
  files: FileText,
  knowledge: BookOpen,
  sharepoint: FolderOpen,
  drive: HardDrive,
};

/** A labelled part of the card; `id` is its label's, which names its list for screen readers. */
function Part({ id, label, children }: Readonly<{ id: string; label: string; children: ReactNode }>) {
  return (
    <div className="space-y-1">
      <Text variant="caption" tone="tertiary" id={id}>
        {label}
      </Text>
      {children}
    </div>
  );
}

/** One place the run looks. Plain text, so a long list of sites wraps on a phone. */
function SourceItem({ icon: Icon, children }: Readonly<{ icon: LucideIcon; children: string }>) {
  return (
    <li className="flex items-start gap-2">
      <Icon size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-content-tertiary" />
      <Text variant="body-sm" tone="secondary" className="min-w-0 break-words">
        {children}
      </Text>
    </li>
  );
}

/** What the run covers: its topics, the assumptions made instead of asking, and where it looks. */
export function ProposalDetails({ proposal }: Readonly<{ proposal: ResearchProposal }>) {
  const { t } = useTranslation("chat", { keyPrefix: "research.proposal" });
  const topicsId = useId();
  const assumptionsId = useId();
  const sourcesId = useId();
  const { topics, assumptions, scope } = proposal;
  const web = scope.domains.length > 0 ? t("webSites", { sites: formatList(scope.domains) }) : t("web");
  // Named when the backend said which apply; an older proposal keeps the general line
  const internal = scope.internal ? proposal.internalSources : [];
  const anySource = scope.web || internal === null || internal.length > 0;
  return (
    <>
      {topics.length > 0 && (
        <Part id={topicsId} label={t("topics")}>
          <ol aria-labelledby={topicsId} className="list-decimal space-y-0.5 pl-5">
            {topics.map((topic) => (
              <Text key={topic} as="li" variant="body-sm" className="break-words">
                {topic}
              </Text>
            ))}
          </ol>
        </Part>
      )}
      {assumptions.length > 0 && (
        <Part id={assumptionsId} label={t("assumptions")}>
          <ul aria-labelledby={assumptionsId} className="list-disc space-y-0.5 pl-5">
            {assumptions.map((assumption) => (
              <Text key={assumption} as="li" variant="body-sm" tone="secondary" className="break-words">
                {assumption}
              </Text>
            ))}
          </ul>
        </Part>
      )}
      {anySource && (
        <Part id={sourcesId} label={t("sources")}>
          <ul aria-labelledby={sourcesId} className="space-y-0.5">
            {scope.web && <SourceItem icon={Globe}>{web}</SourceItem>}
            {internal === null && <SourceItem icon={FileText}>{t("internal")}</SourceItem>}
            {internal?.map((source) => (
              <SourceItem key={source} icon={INTERNAL_ICONS[source]}>
                {t(`internalSource.${source}`)}
              </SourceItem>
            ))}
            {scope.timeRange && <SourceItem icon={CalendarRange}>{t(`timeRange.${scope.timeRange}`)}</SourceItem>}
          </ul>
        </Part>
      )}
    </>
  );
}
